import React, { useEffect, useState } from "react";
import { TrendingUp, Download, Upload, Gift, RefreshCw, Clock, ArrowRightLeft, Package, Target, Zap, CheckCircle } from "lucide-react";
import walletService from "../services/walletService";
import apiService from "../services/api";
import { useTranslation } from "react-i18next";

export default function RecentActivity() {
  const { t } = useTranslation();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadActivities();
  }, []);

  const loadActivities = async () => {
    setLoading(true);
    try {
      const [deposits, withdrawals, payouts, transactions, spotOrders, futuresPositions] = await Promise.allSettled([
        walletService.getDepositHistory(),
        walletService.getWithdrawalHistory(),
        apiService.get('/investments/payouts/history'),
        apiService.get('/wallets/transactions/history'),
        apiService.get('/spot/orders').catch(() => ({ data: [] })),
        apiService.get('/futures/positions').catch(() => ({ data: [] })),
      ]);

      const all = [];

      // Dépôts
      if (deposits.status === 'fulfilled' && deposits.value) {
        const data = Array.isArray(deposits.value) ? deposits.value : (deposits.value.data || []);
        data.slice(0, 3).forEach(d => {
          all.push({
            type: "deposit",
            icon: <Download className="w-4 h-4 text-emerald-400" />,
            title: t('dashboard.activity.deposit_received'),
            amount: `+${parseFloat(d.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })} USDT`,
            date: formatDate(d.created_at),
            description: d.asset || "USDT"
          });
        });
      }

      // Retraits
      if (withdrawals.status === 'fulfilled' && withdrawals.value) {
        const data = Array.isArray(withdrawals.value) ? withdrawals.value : (withdrawals.value.data || []);
        data.slice(0, 3).forEach(w => {
          all.push({
            type: "withdrawal",
            icon: <Upload className="w-4 h-4 text-rose-400" />,
            title: t('dashboard.activity.withdrawal'),
            amount: `-${parseFloat(w.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })} USDT`,
            date: formatDate(w.created_at),
            description: w.asset || "USDT"
          });
        });
      }

      // Paiements investissements
      if (payouts.status === 'fulfilled' && payouts.value?.success) {
        (payouts.value.data || []).slice(0, 3).forEach(p => {
          all.push({
            type: "payout",
            icon: <Zap className="w-4 h-4 text-amber-400" />,
            title: `Pack — ${p.plan_name || `#${p.investment_id}`}`,
            amount: `+${parseFloat(p.amount || p.payout_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 4 })}`,
            date: formatDate(p.paid_at || p.created_at),
            description: p.asset_symbol || "USDT"
          });
        });
      }

      // Transferts
      if (transactions.status === 'fulfilled' && transactions.value) {
        const data = Array.isArray(transactions.value) ? transactions.value : (transactions.value.data || []);
        data.filter(tx => tx.transaction_type === 'transfer').slice(0, 3).forEach(tx => {
          all.push({
            type: "transfer",
            icon: <ArrowRightLeft className="w-4 h-4 text-indigo-400" />,
            title: "Transfert",
            amount: `${parseFloat(tx.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`,
            date: formatDate(tx.created_at),
            description: `${tx.from_wallet || ''} → ${tx.to_wallet || ''}` || tx.asset || "USDT"
          });
        });
      }

      // Ordres Spot
      if (spotOrders.status === 'fulfilled' && spotOrders.value) {
        const data = Array.isArray(spotOrders.value) ? spotOrders.value : (spotOrders.value.data || []);
        data.slice(0, 2).forEach(o => {
          all.push({
            type: "spot",
            icon: <Package className="w-4 h-4 text-cyan-400" />,
            title: `Spot ${o.side === 'buy' ? 'Achat' : 'Vente'} ${o.symbol || o.pair}`,
            amount: `${o.side === 'buy' ? '+' : '-'}${parseFloat(o.quantity || o.filled_quantity || 0).toLocaleString()}`,
            date: formatDate(o.created_at),
            description: o.status || 'filled'
          });
        });
      }

      // Positions Futures
      if (futuresPositions.status === 'fulfilled' && futuresPositions.value) {
        const data = Array.isArray(futuresPositions.value) ? futuresPositions.value : (futuresPositions.value.data || []);
        data.slice(0, 2).forEach(pos => {
          all.push({
            type: "futures",
            icon: <Target className="w-4 h-4 text-violet-400" />,
            title: `Futures ${pos.side || 'Long'} ${pos.symbol || pos.pair}`,
            amount: `${parseFloat(pos.unrealized_pnl || 0) >= 0 ? '+' : ''}${parseFloat(pos.unrealized_pnl || 0).toLocaleString()}`,
            date: formatDate(pos.created_at || pos.opened_at),
            description: `${pos.leverage || 1}x • ${pos.status || 'open'}`
          });
        });
      }

      // Trier par date et limiter
      all.sort((a, b) => new Date(b.date) - new Date(a.date));
      setActivities(all.slice(0, 8));

    } catch (error) {
      console.error("Error loading activities:", error);
      setActivities(getDefaultActivities());
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return t('dashboard.activity.recently');
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffMins < 60) return `${diffMins} ${t('dashboard.activity.time_min')}`;
    if (diffHours < 24) return `${diffHours} ${t('dashboard.activity.time_h')}`;
    if (diffDays === 1) return `1 ${t('dashboard.activity.time_day')}`;
    return `${diffDays} ${t('dashboard.activity.time_days')}`;
  };

  const getDefaultActivities = () => [
    {
      type: "deposit",
      icon: <Download className="w-4 h-4 text-emerald-400" />,
      title: t('dashboard.activity.initial_deposit'),
      amount: "+1,000.00 USDT",
      date: `1 ${t('dashboard.activity.time_day')}`,
      description: "BTC"
    },
    {
      type: "info",
      icon: <Gift className="w-4 h-4 text-indigo-400" />,
      title: t('dashboard.activity.welcome'),
      amount: "+10.00 USDT",
      date: `1 ${t('dashboard.activity.time_day')}`,
      description: t('dashboard.activity.welcome_bonus')
    }
  ];

  const getActivityIconBg = (type) => {
    switch (type) {
      case 'deposit': return 'bg-emerald-500/15 border-emerald-500/25';
      case 'withdrawal': return 'bg-rose-500/15 border-rose-500/25';
      case 'payout': return 'bg-amber-500/15 border-amber-500/25';
      case 'transfer': return 'bg-indigo-500/15 border-indigo-500/25';
      case 'spot': return 'bg-cyan-500/15 border-cyan-500/25';
      case 'futures': return 'bg-violet-500/15 border-violet-500/25';
      default: return 'bg-indigo-500/15 border-indigo-500/25';
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="flex items-center gap-4 animate-pulse">
            <div className="w-10 h-10 bg-white/5 rounded-xl" />
            <div className="flex-1 space-y-2">
              <div className="h-2 bg-white/5 rounded w-1/2" />
              <div className="h-1.5 bg-white/5 rounded w-1/3 opacity-50" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <Clock size={14} className="text-indigo-400" />
          <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">{t('dashboard.activity.title')}</span>
        </div>
        <button onClick={loadActivities} className="p-1.5 hover:bg-white/5 rounded-lg text-slate-500 hover:text-indigo-400 transition-all group">
          <RefreshCw size={14} className="group-hover:rotate-180 transition-transform duration-500" />
        </button>
      </div>
      
      <div className="space-y-1">
        {activities.length > 0 ? (
          activities.map((activity, index) => (
            <div key={index} className="group p-3 rounded-xl hover:bg-white/[0.03] transition-all border border-transparent hover:border-white/5">
              <div className="flex items-center gap-4">
                <div className={`p-2.5 rounded-xl border ${getActivityIconBg(activity.type)} group-hover:scale-110 transition-transform`}>
                  {activity.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <h4 className="text-[11px] font-black text-white uppercase tracking-tight truncate">{activity.title}</h4>
                    <span className={`text-[11px] font-black font-outfit ${
                      activity.amount.startsWith('+') ? 'text-emerald-400' : 
                      activity.amount.startsWith('-') ? 'text-rose-400' : 'text-slate-300'
                    }`}>
                      {activity.amount}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <p className="text-[9px] text-slate-500 font-bold uppercase tracking-widest opacity-60 truncate">{activity.description}</p>
                    <span className="text-[9px] text-slate-600 font-black uppercase">{activity.date}</span>
                  </div>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="py-12 text-center">
            <TrendingUp className="w-8 h-8 text-slate-800 mx-auto mb-4" />
            <h4 className="text-xs font-black text-slate-600 uppercase tracking-widest mb-1">{t('dashboard.activity.no_activity')}</h4>
            <p className="text-[10px] text-slate-700 font-bold uppercase">{t('dashboard.activity.first_tx_prompt')}</p>
          </div>
        )}
      </div>
    </div>
  );
}